import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Admin.css';

function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [activeSection, setActiveSection] = useState('dashboard');
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  
  // Partner management
  const [partners, setPartners] = useState([]);
  const [newPartner, setNewPartner] = useState({ username: '', password: '', email: '', company: '' });
  
  // Company management
  const [companyInfo, setCompanyInfo] = useState({});
  const [editCompany, setEditCompany] = useState({});
  
  // About content management
  const [aboutContent, setAboutContent] = useState({
    title: '',
    description: '',
    mission: '',
    vision: '',
    services: []
  });
  
  // Projects management
  const [projects, setProjects] = useState([]);
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    category: 'residential',
    status: 'planning',
    images: [],
    startDate: '',
    endDate: ''
  });
  const [editingProject, setEditingProject] = useState(null);
  
  // File management
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [projectTitle, setProjectTitle] = useState('');
  const [beforeAfterProjects, setBeforeAfterProjects] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  
  // Status management
  const [statusForm, setStatusForm] = useState({ title: '', description: '', date: '' });
  
  // Messages management
  const [contactMessages, setContactMessages] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);

  // Analytics management
  const [analytics, setAnalytics] = useState({
    totalVisits: 0,
    uniqueVisitors: 0,
    todayVisits: 0,
    weekVisits: 0,
    monthVisits: 0,
    topCountries: [],
    topCities: [],
    topPages: [],
    recentVisitors: []
  });

  // Audit log management
  const [auditLog, setAuditLog] = useState([]);

  // Social media links management
  const [socialLinks, setSocialLinks] = useState({
    facebook: '',
    instagram: '',
    tiktok: '',
    linkedin: '',
    youtube: '',
    twitter: ''
  });

  // Service Pricing management
  const [services, setServices] = useState([]);
  const [serviceForm, setServiceForm] = useState({ serviceName: '', pricePerM2: '' });
  const [editingService, setEditingService] = useState(null);

  // Jobs management
  const [jobs, setJobs] = useState([]);
  const [jobForm, setJobForm] = useState({
    title: '', description: '', requirements: '', salary: '', location: '', type: 'Full-time', status: 'active'
  });
  const [editingJob, setEditingJob] = useState(null);
  const [jobApplications, setJobApplications] = useState([]);

  // Idle timeout - 30 minutes
  useEffect(() => {
    if (!isLoggedIn) return;

    let idleTimer;
    let warningTimer;
    const IDLE_TIME = 2 * 60 * 1000; // 2 minutes (TESTING - change to 30 * 60 * 1000 for production)
    const WARNING_TIME = 1.5 * 60 * 1000; // 1.5 minutes (TESTING - change to 28 * 60 * 1000 for production)

    const handleLogout = async () => {
      try {
        await axios.post('/api/logout');
      } catch (error) {
        console.error('Logout error:', error);
      }
      localStorage.removeItem('adminToken');
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setIsLoggedIn(false);
      setShowIdleWarning(false);
    };

    const resetTimers = () => {
      setShowIdleWarning(false);
      clearTimeout(idleTimer);
      clearTimeout(warningTimer);
      
      warningTimer = setTimeout(() => {
        setShowIdleWarning(true);
      }, WARNING_TIME);
      
      idleTimer = setTimeout(handleLogout, IDLE_TIME);
    };

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => document.addEventListener(event, resetTimers));
    
    resetTimers(); // Start timers

    return () => {
      clearTimeout(idleTimer);
      clearTimeout(warningTimer);
      events.forEach(event => document.removeEventListener(event, resetTimers));
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsLoggedIn(true);
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchPartners(),
        fetchCompanyInfo(),
        fetchAboutContent(),
        fetchProjects(),
        fetchServices(),
        fetchJobs(),
        fetchJobApplications(),
        fetchBeforeAfterProjects(),
        fetchUploadedFiles(),
        fetchContactMessages(),
        fetchQuoteRequests(),
        fetchAnalytics(),
        fetchAuditLog(),
        fetchSocialLinks()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get('/api/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const response = await axios.get('/api/audit-log');
      setAuditLog(response.data);
    } catch (error) {
      console.error('Error fetching audit log:', error);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get('/api/social-links');
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Error fetching social links:', error);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get('/api/company');
      setCompanyInfo(response.data);
      setEditCompany(response.data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get('/api/admin/partners');
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/login', loginForm);
      localStorage.setItem('adminToken', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setIsLoggedIn(true);
      fetchData();
    } catch (error) {
      alert('Invalid admin credentials');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/request-password-reset', { email: resetEmail });
      setResetMessage(response.data.message);
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetMessage('');
        setResetEmail('');
      }, 3000);
    } catch (error) {
      setResetMessage(error.response?.data?.error || 'Failed to send reset email');
    }
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/admin/partners', newPartner);
      setNewPartner({ username: '', password: '', email: '', company: '' });
      fetchPartners();
      alert('Partner created successfully');
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Error creating partner';
      alert(errorMessage);
    }
  };

  const togglePartnerStatus = async (partnerId, currentStatus) => {
    try {
      await axios.put(`/api/admin/partners/${partnerId}/status`, { 
        status: currentStatus === 'active' ? 'locked' : 'active' 
      });
      fetchPartners();
    } catch (error) {
      alert('Error updating partner status');
    }
  };

  const deletePartner = async (partnerId) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await axios.delete(`/api/admin/partners/${partnerId}`);
        fetchPartners();
      } catch (error) {
        alert('Error deleting partner');
      }
    }
  };

  // Service Pricing management functions
  const fetchServices = async () => {
    try {
      const response = await axios.get('/api/calculator-services');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.post('/api/calculator-services', serviceForm);
      setServices([...services, response.data]);
      setServiceForm({ serviceName: '', pricePerM2: '' });
      alert('Service added successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Error adding service');
    }
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.put(`/api/calculator-services/${editingService.id}`, serviceForm);
      setServices(services.map(s => s.id === editingService.id ? response.data : s));
      setServiceForm({ serviceName: '', pricePerM2: '' });
      setEditingService(null);
      alert('Service updated successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Error updating service');
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm({ serviceName: service.serviceName, pricePerM2: service.pricePerM2 });
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    setServiceForm({ serviceName: '', pricePerM2: '' });
  };

  const handleDeleteService = async (serviceId) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        await axios.delete(`/api/calculator-services/${serviceId}`);
        setServices(services.filter(s => s.id !== serviceId));
        alert('Service deleted successfully!');
      } catch (error) {
        alert('Error deleting service');
      }
    }
  };

  // Jobs management functions
  const fetchJobs = async () => {
    try {
      const response = await axios.get('/api/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchJobApplications = async () => {
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.get('/api/job-applications');
      setJobApplications(response.data);
    } catch (error) {
      console.error('Error fetching job applications:', error);
    }
  };

  const handleAddJob = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.post('/api/jobs', jobForm);
      setJobs([...jobs, response.data]);
      setJobForm({ title: '', description: '', requirements: '', salary: '', location: '', type: 'Full-time', status: 'active' });
      alert('Job posted successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Error adding job');
    }
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      const response = await axios.put(`/api/jobs/${editingJob.id}`, jobForm);
      setJobs(jobs.map(j => j.id === editingJob.id ? response.data : j));
      setJobForm({ title: '', description: '', requirements: '', salary: '', location: '', type: 'Full-time', status: 'active' });
      setEditingJob(null);
      alert('Job updated successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Error updating job');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({
      title: job.title,
      description: job.description,
      requirements: job.requirements || '',
      salary: job.salary || '',
      location: job.location || '',
      type: job.type || 'Full-time',
      status: job.status || 'active'
    });
  };

  const handleCancelJobEdit = () => {
    setEditingJob(null);
    setJobForm({ title: '', description: '', requirements: '', salary: '', location: '', type: 'Full-time', status: 'active' });
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        await axios.delete(`/api/jobs/${jobId}`);
        setJobs(jobs.filter(j => j.id !== jobId));
        alert('Job deleted successfully!');
      } catch (error) {
        alert('Error deleting job');
      }
    }
  };

  // Company management functions
  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.put('/api/company', editCompany);
      setCompanyInfo(editCompany);
      alert('Company information updated successfully');
    } catch (error) {
      alert('Error updating company information');
    }
  };

  const handleUpdateSocialLinks = async (e) => {
    e.preventDefault();
    try {
      await axios.put('/api/social-links', socialLinks);
      alert('Social media links updated successfully');
    } catch (error) {
      alert('Error updating social links');
    }
  };

  // About content management functions
  const fetchAboutContent = async () => {
    try {
      const response = await axios.get('/api/about');
      setAboutContent(response.data);
    } catch (error) {
      console.error('Error fetching about content:', error);
      // Set default content if API fails
      setAboutContent({
        title: 'About Our Company',
        description: 'We are Gjenerali SHPK Construction, a leading construction company in Albania with over 15 years of experience.',
        mission: 'Our mission is to deliver exceptional construction services that exceed client expectations.',
        vision: 'To be the leading construction company in Albania, known for quality and innovation.',
        services: ['Residential Construction', 'Commercial Building', 'Renovations & Remodeling', 'Project Management']
      });
    }
  };

  const handleUpdateAbout = async (e) => {
    e.preventDefault();
    try {
      // Ensure token is set
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('Sending about content:', aboutContent);
      console.log('Auth header:', axios.defaults.headers.common['Authorization']);
      
      const response = await axios.put('/api/about', aboutContent);
      console.log('Response:', response.data);
      alert('About content updated successfully');
    } catch (error) {
      console.error('Full error:', error);
      console.error('Error response:', error.response);
      if (error.response?.status === 403) {
        alert('Authentication failed. Please log in again.');
        setIsLoggedIn(false);
      } else {
        alert(`Error updating about content: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  // Projects management functions
  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        alert('Please login first');
        return;
      }

      console.log('Project form data:', projectForm);
      console.log('Images to upload:', projectForm.images);

      const formData = new FormData();
      formData.append('title', projectForm.title);
      formData.append('description', projectForm.description);
      formData.append('category', projectForm.category);
      formData.append('status', projectForm.status);
      formData.append('startDate', projectForm.startDate);
      formData.append('endDate', projectForm.endDate);
      
      // Append multiple images
      if (projectForm.images && projectForm.images.length > 0) {
        Array.from(projectForm.images).forEach((image, index) => {
          console.log(`Adding image ${index}:`, image.name);
          formData.append('images', image);
        });
      }

      console.log('Sending request to /api/projects');
      const response = await axios.post('/api/projects', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Response received:', response.data);
      setProjects([...projects, response.data]);
      setProjectForm({
        title: '', description: '', category: 'residential', status: 'planning',
        images: [], startDate: '', endDate: ''
      });
      alert('Project added successfully!');
    } catch (error) {
      console.error('Full error details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      alert(`Error adding project: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const formData = new FormData();
      formData.append('title', projectForm.title);
      formData.append('description', projectForm.description);
      formData.append('category', projectForm.category);
      formData.append('status', projectForm.status);
      formData.append('startDate', projectForm.startDate);
      formData.append('endDate', projectForm.endDate);
      
      // Append multiple images if new ones are selected
      if (projectForm.images && projectForm.images.length > 0) {
        Array.from(projectForm.images).forEach((image, index) => {
          formData.append('images', image);
        });
      }

      const response = await axios.put(`/api/projects/${editingProject.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProjects(projects.map(p => p.id === editingProject.id ? response.data : p));
      setEditingProject(null);
      setProjectForm({
        title: '', description: '', category: 'residential', status: 'planning',
        images: [], startDate: '', endDate: ''
      });
      alert('Project updated successfully!');
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Error updating project');
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        await axios.delete(`/api/projects/${id}`);
        setProjects(projects.filter(p => p.id !== id));
        alert('Project deleted successfully!');
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Error deleting project');
      }
    }
  };

  const startEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({
      title: project.title || '',
      description: project.description || '',
      category: project.category || 'residential',
      status: project.status || 'planning',
      images: [],
      startDate: project.startDate || '',
      endDate: project.endDate || ''
    });
  };

  const cancelEdit = () => {
    setEditingProject(null);
    setProjectForm({
      title: '', description: '', category: 'residential', status: 'planning',
      images: [], startDate: '', endDate: ''
    });
  };

  // File upload functions
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles) return;

    const formData = new FormData();
    for (let file of selectedFiles) {
      formData.append('files', file);
    }

    try {
      await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSelectedFiles(null);
      alert('Files uploaded successfully');
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || 'Error uploading files';
      alert(errorMessage);
    }
  };

  const handleBeforeAfterUpload = async (e) => {
    e.preventDefault();
    if (!beforeImage || !afterImage) {
      alert('Please select both before and after images');
      return;
    }

    const formData = new FormData();
    formData.append('beforeImage', beforeImage);
    formData.append('afterImage', afterImage);
    formData.append('title', projectTitle);

    try {
      await axios.post('/api/before-after', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setBeforeImage(null);
      setAfterImage(null);
      setProjectTitle('');
      alert('Before/After project uploaded successfully! Check the Gallery > Before/After tab to see it.');
      
      // Clear the file inputs
      const beforeInput = document.querySelector('input[type="file"][accept="image/*"]:first-of-type');
      const afterInput = document.querySelector('input[type="file"][accept="image/*"]:last-of-type');
      if (beforeInput) beforeInput.value = '';
      if (afterInput) afterInput.value = '';
      
      // Refresh the list
      await fetchBeforeAfterProjects();
      
    } catch (error) {
      console.error('Before/After upload error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Authentication failed. Please login again.');
        setIsLoggedIn(false);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('token');
      } else {
        const errorMessage = error.response?.data?.error || 'Error uploading before/after project';
        alert(errorMessage);
      }
    }
  };

  const fetchBeforeAfterProjects = async () => {
    try {
      const response = await axios.get('/api/before-after');
      setBeforeAfterProjects(response.data);
    } catch (error) {
      console.error('Error fetching before/after projects:', error);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      const response = await axios.get('/api/files');
      setUploadedFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchContactMessages = async () => {
    try {
      const response = await axios.get('/api/contact-messages');
      setContactMessages(response.data);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    }
  };

  const fetchQuoteRequests = async () => {
    try {
      const response = await axios.get('/api/quote-requests');
      setQuoteRequests(response.data);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
    }
  };

  const deleteContactMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await axios.delete(`/api/contact-messages/${id}`);
      alert('Message deleted successfully!');
      await fetchContactMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  const deleteQuoteRequest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this quote request?')) return;
    try {
      await axios.delete(`/api/quote-requests/${id}`);
      alert('Quote request deleted successfully!');
      await fetchQuoteRequests();
    } catch (error) {
      console.error('Error deleting quote request:', error);
      alert('Failed to delete quote request');
    }
  };

  const deleteBeforeAfterProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await axios.delete(`/api/before-after/${id}`);
      alert('Project deleted successfully!');
      await fetchBeforeAfterProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error deleting project');
    }
  };

  const deleteFile = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    try {
      await axios.delete(`/api/files/${filename}`);
      alert('File deleted successfully!');
      await fetchUploadedFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file');
    }
  };

  // Status management
  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/status', statusForm);
      setStatusForm({ title: '', description: '', date: '' });
      alert('Project status added successfully');
    } catch (error) {
      alert('Error adding project status');
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="admin-login">
        <div className="login-container">
          <h1>🔐 Admin Login</h1>
          
          {!showForgotPassword ? (
            <>
              <form onSubmit={handleLogin}>
                <input
                  type="text"
                  placeholder="Admin Username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                  required
                />
                <input
                  type="password"
                  placeholder="Admin Password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  required
                />
                <button type="submit">Login as Admin</button>
              </form>
              <button 
                onClick={() => setShowForgotPassword(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1e40af',
                  cursor: 'pointer',
                  marginTop: '10px',
                  textDecoration: 'underline'
                }}
              >
                Forgot Password?
              </button>
            </>
          ) : (
            <>
              <h2>Reset Password</h2>
              <form onSubmit={handleForgotPassword}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
                <button type="submit">Send Reset Link</button>
              </form>
              {resetMessage && (
                <p style={{ color: resetMessage.includes('sent') ? 'green' : 'red', marginTop: '10px' }}>
                  {resetMessage}
                </p>
              )}
              <button 
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetMessage('');
                  setResetEmail('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1e40af',
                  cursor: 'pointer',
                  marginTop: '10px',
                  textDecoration: 'underline'
                }}
              >
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {showIdleWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ff9800',
          color: 'white',
          padding: '15px',
          textAlign: 'center',
          zIndex: 10000,
          fontWeight: 'bold',
          fontSize: '16px'
        }}>
          ⚠️ You will be logged out in 2 minutes due to inactivity. Move your mouse to stay logged in.
        </div>
      )}
      <header className="admin-header">
        <h1>🏗️ Admin Dashboard</h1>
        <div className="header-buttons">
          <button onClick={() => window.location.href = '/'} className="home-btn">
            🏠 Home
          </button>
          <button onClick={async () => {
            try {
              await axios.post('/api/logout');
            } catch (error) {
              console.error('Logout error:', error);
            }
            localStorage.removeItem('adminToken');
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            setIsLoggedIn(false);
          }}>Logout</button>
        </div>
      </header>

      <nav className="admin-nav">
        <button 
          className={activeSection === 'dashboard' ? 'active' : ''}
          onClick={() => setActiveSection('dashboard')}
        >
          📊 Dashboard
        </button>
        <button 
          className={activeSection === 'company' ? 'active' : ''}
          onClick={() => setActiveSection('company')}
        >
          🏢 Company Info
        </button>
        <button 
          className={activeSection === 'about' ? 'active' : ''}
          onClick={() => setActiveSection('about')}
        >
          📋 About Content
        </button>
        <button 
          className={activeSection === 'projectsManagement' ? 'active' : ''}
          onClick={() => setActiveSection('projectsManagement')}
        >
          🏗️ Projects Management
        </button>
        <button 
          className={activeSection === 'files' ? 'active' : ''}
          onClick={() => setActiveSection('files')}
        >
          📁 File Management
        </button>
        <button 
          className={activeSection === 'servicePricing' ? 'active' : ''}
          onClick={() => setActiveSection('servicePricing')}
        >
          💰 Service Pricing
        </button>
        <button 
          className={activeSection === 'jobs' ? 'active' : ''}
          onClick={() => setActiveSection('jobs')}
        >
          💼 Jobs Management
        </button>
        <button 
          className={activeSection === 'projects' ? 'active' : ''}
          onClick={() => setActiveSection('projects')}
        >
          🏗️ Projects
        </button>
        <button 
          className={activeSection === 'partners' ? 'active' : ''}
          onClick={() => setActiveSection('partners')}
        >
          👥 Partners
        </button>
        <button 
          className={activeSection === 'messages' ? 'active' : ''}
          onClick={() => setActiveSection('messages')}
        >
          📧 Messages {contactMessages.length + quoteRequests.length > 0 && `(${contactMessages.length + quoteRequests.length})`}
        </button>
      </nav>

      <div className="admin-content">
        {activeSection === 'dashboard' && (
          <section className="dashboard">
            <h2>📊 Analytics Dashboard</h2>
            
            {/* Main Stats */}
            <div className="stats-grid">
              <div className="stat-card primary">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <h3>Total Visits</h3>
                  <p className="stat-number">{analytics.totalVisits.toLocaleString()}</p>
                </div>
              </div>
              <div className="stat-card success">
                <div className="stat-icon">🌟</div>
                <div className="stat-content">
                  <h3>Unique Visitors</h3>
                  <p className="stat-number">{analytics.uniqueVisitors.toLocaleString()}</p>
                </div>
              </div>
              <div className="stat-card info">
                <div className="stat-icon">📅</div>
                <div className="stat-content">
                  <h3>Today</h3>
                  <p className="stat-number">{analytics.todayVisits}</p>
                </div>
              </div>
              <div className="stat-card warning">
                <div className="stat-icon">📈</div>
                <div className="stat-content">
                  <h3>This Week</h3>
                  <p className="stat-number">{analytics.weekVisits}</p>
                </div>
              </div>
              <div className="stat-card secondary">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <h3>This Month</h3>
                  <p className="stat-number">{analytics.monthVisits}</p>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="analytics-grid">
              {/* Top Countries */}
              <div className="analytics-card">
                <h3>🌍 Top Countries</h3>
                <div className="analytics-list">
                  {analytics.topCountries.map((item, index) => (
                    <div key={index} className="analytics-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="label">{item.country}</span>
                      <span className="value">{item.count}</span>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{width: `${(item.count / analytics.totalVisits * 100)}%`}}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Cities */}
              <div className="analytics-card">
                <h3>🏙️ Top Cities</h3>
                <div className="analytics-list">
                  {analytics.topCities.map((item, index) => (
                    <div key={index} className="analytics-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="label">{item.city}</span>
                      <span className="value">{item.count}</span>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{width: `${(item.count / analytics.totalVisits * 100)}%`}}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Pages */}
              <div className="analytics-card">
                <h3>📄 Popular Pages</h3>
                <div className="analytics-list">
                  {analytics.topPages.map((item, index) => (
                    <div key={index} className="analytics-item">
                      <span className="rank">#{index + 1}</span>
                      <span className="label">{item.page === '/' ? 'Home' : item.page}</span>
                      <span className="value">{item.count}</span>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{width: `${(item.count / analytics.totalVisits * 100)}%`}}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Visitors */}
              <div className="analytics-card">
                <h3>🕐 Recent Visitors</h3>
                <div className="recent-visitors">
                  {analytics.recentVisitors.map((visitor, index) => (
                    <div key={index} className="visitor-item">
                      <div className="visitor-location">
                        <strong>{visitor.city}, {visitor.country}</strong>
                        <span className="visitor-page">{visitor.page === '/' ? 'Home' : visitor.page}</span>
                      </div>
                      <div className="visitor-time">
                        {new Date(visitor.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Audit Log */}
              <div className="analytics-card">
                <h3>📋 Audit Log</h3>
                <div className="audit-log">
                  {auditLog.map((entry, index) => (
                    <div key={index} className="audit-entry">
                      <div className="audit-action">
                        <span className={`audit-badge ${entry.action.toLowerCase().replace(/_/g, '-')}`}>
                          {entry.action.replace(/_/g, ' ')}
                        </span>
                        <strong>{entry.user}</strong>
                      </div>
                      {entry.details && <div className="audit-details">{entry.details}</div>}
                      <div className="audit-time">
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* GDPR Data Management */}
              <div className="analytics-card">
                <h3>🔒 GDPR Data Management</h3>
                <div className="gdpr-section">
                  <div className="gdpr-info">
                    <p><strong>Analytics Entries:</strong> {analytics.totalVisits || 0}</p>
                    <p><strong>Retention Period:</strong> 90 days (auto-delete)</p>
                    <p><strong>Audit Logs:</strong> {auditLog.length} entries</p>
                  </div>
                  <div className="gdpr-actions">
                    <button 
                      onClick={async () => {
                        if (window.confirm('Delete all analytics data? This cannot be undone.')) {
                          try {
                            await axios.delete('/api/analytics-data');
                            alert('Analytics data deleted successfully');
                            fetchAnalytics();
                          } catch (error) {
                            alert('Error deleting analytics data');
                          }
                        }
                      }}
                      className="btn-danger"
                    >
                      🗑️ Delete Analytics Data
                    </button>
                    <button 
                      onClick={async () => {
                        try {
                          const response = await axios.get('/api/export-analytics');
                          const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `analytics-${new Date().toISOString().split('T')[0]}.json`;
                          a.click();
                        } catch (error) {
                          alert('Error exporting analytics data');
                        }
                      }}
                      className="btn-secondary"
                    >
                      📥 Export Analytics Data
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeSection === 'company' && (
          <section className="company-section">
            <h2>Company Information</h2>
            <form onSubmit={handleUpdateCompany}>
              <input
                type="text"
                placeholder="Company Name"
                value={editCompany.name || ''}
                onChange={(e) => setEditCompany({...editCompany, name: e.target.value})}
              />
              <input
                type="text"
                placeholder="Phone"
                value={editCompany.phone || ''}
                onChange={(e) => setEditCompany({...editCompany, phone: e.target.value})}
              />
              <input
                type="email"
                placeholder="Email"
                value={editCompany.email || ''}
                onChange={(e) => setEditCompany({...editCompany, email: e.target.value})}
              />
              <input
                type="text"
                placeholder="Address"
                value={editCompany.address || ''}
                onChange={(e) => setEditCompany({...editCompany, address: e.target.value})}
              />
              <textarea
                placeholder="Description"
                value={editCompany.description || ''}
                onChange={(e) => setEditCompany({...editCompany, description: e.target.value})}
              />
              <button type="submit">Update Company Info</button>
            </form>

            <div style={{ marginTop: '40px' }}>
              <h2>📱 Social Media Links</h2>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                Add your social media profile URLs. Leave blank if not using that platform.
              </p>
              <form onSubmit={handleUpdateSocialLinks}>
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Facebook
                    </label>
                    <input
                      type="url"
                      placeholder="https://facebook.com/yourpage"
                      value={socialLinks.facebook}
                      onChange={(e) => setSocialLinks({...socialLinks, facebook: e.target.value})}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Instagram
                    </label>
                    <input
                      type="url"
                      placeholder="https://instagram.com/yourprofile"
                      value={socialLinks.instagram}
                      onChange={(e) => setSocialLinks({...socialLinks, instagram: e.target.value})}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      TikTok
                    </label>
                    <input
                      type="url"
                      placeholder="https://tiktok.com/@yourprofile"
                      value={socialLinks.tiktok}
                      onChange={(e) => setSocialLinks({...socialLinks, tiktok: e.target.value})}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      LinkedIn
                    </label>
                    <input
                      type="url"
                      placeholder="https://linkedin.com/company/yourcompany"
                      value={socialLinks.linkedin}
                      onChange={(e) => setSocialLinks({...socialLinks, linkedin: e.target.value})}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      YouTube
                    </label>
                    <input
                      type="url"
                      placeholder="https://youtube.com/@yourchannel"
                      value={socialLinks.youtube}
                      onChange={(e) => setSocialLinks({...socialLinks, youtube: e.target.value})}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Twitter / X
                    </label>
                    <input
                      type="url"
                      placeholder="https://twitter.com/yourprofile"
                      value={socialLinks.twitter}
                      onChange={(e) => setSocialLinks({...socialLinks, twitter: e.target.value})}
                    />
                  </div>
                </div>
                <button type="submit" style={{ marginTop: '20px' }}>
                  💾 Save Social Links
                </button>
              </form>
            </div>
          </section>
        )}

        {activeSection === 'about' && (
          <section className="about-section">
            <h2>About Content Management</h2>
            <form onSubmit={handleUpdateAbout}>
              <input
                type="text"
                placeholder="About Page Title"
                value={aboutContent.title || ''}
                onChange={(e) => setAboutContent({...aboutContent, title: e.target.value})}
              />
              <textarea
                placeholder="Company Description"
                value={aboutContent.description || ''}
                onChange={(e) => setAboutContent({...aboutContent, description: e.target.value})}
                rows="4"
              />
              <textarea
                placeholder="Mission Statement"
                value={aboutContent.mission || ''}
                onChange={(e) => setAboutContent({...aboutContent, mission: e.target.value})}
                rows="3"
              />
              <textarea
                placeholder="Vision Statement"
                value={aboutContent.vision || ''}
                onChange={(e) => setAboutContent({...aboutContent, vision: e.target.value})}
                rows="3"
              />
              <div className="services-section">
                <h3>Services List</h3>
                <p>Enter each service on a new line:</p>
                <textarea
                  placeholder="Residential Construction&#10;Commercial Building&#10;Renovations & Remodeling&#10;Project Management"
                  value={aboutContent.services ? aboutContent.services.join('\n') : ''}
                  onChange={(e) => setAboutContent({
                    ...aboutContent, 
                    services: e.target.value.split('\n').filter(service => service.trim())
                  })}
                  rows="6"
                />
              </div>
              <button type="submit">Update About Content</button>
            </form>
          </section>
        )}

        {activeSection === 'projectsManagement' && (
          <section className="projects-management-section">
            <h2>Projects Management</h2>
            
            {/* Add/Edit Project Form */}
            <div className="project-form-container">
              <h3>{editingProject ? 'Edit Project' : 'Add New Project'}</h3>
              <form onSubmit={editingProject ? handleUpdateProject : handleAddProject}>
                <input
                  type="text"
                  placeholder="Project Title"
                  value={projectForm.title}
                  onChange={(e) => setProjectForm({...projectForm, title: e.target.value})}
                  required
                />
                <textarea
                  placeholder="Project Description"
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                  rows="4"
                  required
                />
                <select
                  value={projectForm.category}
                  onChange={(e) => setProjectForm({...projectForm, category: e.target.value})}
                >
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="renovation">Renovation</option>
                  <option value="infrastructure">Infrastructure</option>
                </select>
                <select
                  value={projectForm.status}
                  onChange={(e) => setProjectForm({...projectForm, status: e.target.value})}
                >
                  <option value="planning">Planning</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on-hold">On Hold</option>
                </select>
                <input
                  type="date"
                  placeholder="Start Date"
                  value={projectForm.startDate}
                  onChange={(e) => setProjectForm({...projectForm, startDate: e.target.value})}
                />
                <input
                  type="date"
                  placeholder="End Date"
                  value={projectForm.endDate}
                  onChange={(e) => setProjectForm({...projectForm, endDate: e.target.value})}
                />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setProjectForm({...projectForm, images: e.target.files})}
                />
                <small>You can select multiple images (hold Ctrl/Cmd to select multiple files)</small>
                <div className="form-buttons">
                  <button type="submit">
                    {editingProject ? 'Update Project' : 'Add Project'}
                  </button>
                  {editingProject && (
                    <button type="button" onClick={cancelEdit}>Cancel</button>
                  )}
                </div>
              </form>
            </div>

            {/* Projects List */}
            <div className="projects-list">
              <h3>Existing Projects</h3>
              {projects.length === 0 ? (
                <p>No projects found. Add your first project above!</p>
              ) : (
                <div className="projects-grid">
                  {projects.map(project => (
                    <div key={project.id} className="project-card">
                      <h4>{project.title}</h4>
                      <p><strong>Category:</strong> {project.category}</p>
                      <p><strong>Status:</strong> {project.status}</p>
                      <p>{project.description}</p>
                      {project.startDate && <p><strong>Start:</strong> {project.startDate}</p>}
                      {project.endDate && <p><strong>End:</strong> {project.endDate}</p>}
                      {project.images && project.images.length > 0 && (
                        <div className="project-images">
                          {project.images.map((image, index) => (
                            <img 
                              key={index}
                              src={image} 
                              alt={`${project.title} ${index + 1}`} 
                              style={{width: '80px', height: '80px', objectFit: 'cover', margin: '2px'}} 
                            />
                          ))}
                        </div>
                      )}
                      <div className="project-actions">
                        <button onClick={() => startEditProject(project)}>Edit</button>
                        <button onClick={() => handleDeleteProject(project.id)} className="delete-btn">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {activeSection === 'files' && (
          <section className="files-section">
            <h2>File Management</h2>
            
            <div className="upload-section">
              <h3>Upload Gallery Files</h3>
              <form onSubmit={handleFileUpload}>
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  onChange={(e) => setSelectedFiles(e.target.files)}
                />
                <button type="submit" disabled={!selectedFiles}>Upload Files</button>
              </form>
            </div>

            <div className="before-after-section">
              <h3>Before & After Projects</h3>
              <form onSubmit={handleBeforeAfterUpload}>
                <input
                  type="text"
                  placeholder="Project Title"
                  value={projectTitle}
                  onChange={(e) => setProjectTitle(e.target.value)}
                  required
                />
                <div className="image-uploads">
                  <div>
                    <label>Before Image:</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setBeforeImage(e.target.files[0])}
                      required
                    />
                  </div>
                  <div>
                    <label>After Image:</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setAfterImage(e.target.files[0])}
                      required
                    />
                  </div>
                </div>
                <button type="submit" disabled={!beforeImage || !afterImage}>Upload Project</button>
              </form>

              <div className="projects-list">
                <h4>Existing Projects</h4>
                {beforeAfterProjects.length > 0 ? (
                  <div className="gallery-grid">
                    {beforeAfterProjects.map(project => (
                      <div key={project.id} className="gallery-item">
                        <h5>{project.title}</h5>
                        <div className="before-after-images">
                          <div>
                            <p>Before:</p>
                            <img src={`/uploads/${project.beforeImage}`} alt="Before" />
                          </div>
                          <div>
                            <p>After:</p>
                            <img src={`/uploads/${project.afterImage}`} alt="After" />
                          </div>
                        </div>
                        <button 
                          className="delete-btn" 
                          onClick={() => deleteBeforeAfterProject(project.id)}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No projects yet</p>
                )}
              </div>
            </div>

            <div className="uploaded-files-section">
              <h3>Uploaded Files</h3>
              {uploadedFiles.length > 0 ? (
                <div className="files-grid">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="file-item">
                      {file.type?.startsWith('image/') ? (
                        <img src={`/uploads/${file.filename}`} alt={file.originalname} />
                      ) : (
                        <div className="file-icon">📄</div>
                      )}
                      <p>{file.originalname || file.filename}</p>
                      <button 
                        className="delete-btn" 
                        onClick={() => deleteFile(file.filename)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No files uploaded yet</p>
              )}
            </div>
          </section>
        )}

        {activeSection === 'projects' && (
          <section className="projects-section">
            <h2>Project Status Management</h2>
            <form onSubmit={handleStatusSubmit}>
              <input
                type="text"
                placeholder="Status Title"
                value={statusForm.title}
                onChange={(e) => setStatusForm({...statusForm, title: e.target.value})}
                required
              />
              <textarea
                placeholder="Status Description"
                value={statusForm.description}
                onChange={(e) => setStatusForm({...statusForm, description: e.target.value})}
                required
              />
              <input
                type="date"
                value={statusForm.date}
                onChange={(e) => setStatusForm({...statusForm, date: e.target.value})}
                required
              />
              <button type="submit">Add Status Update</button>
            </form>
          </section>
        )}

        {activeSection === 'servicePricing' && (
          <section className="service-pricing">
            <h2>💰 Service Pricing Management</h2>
            <p>Manage calculator services and pricing per m²</p>

            <form onSubmit={editingService ? handleUpdateService : handleAddService} className="service-form">
              <h3>{editingService ? 'Edit Service' : 'Add New Service'}</h3>
              <input
                type="text"
                placeholder="Service Name (e.g., Bathroom Renovation)"
                value={serviceForm.serviceName}
                onChange={(e) => setServiceForm({...serviceForm, serviceName: e.target.value})}
                required
              />
              <input
                type="number"
                placeholder="Price per m² (EUR)"
                value={serviceForm.pricePerM2}
                onChange={(e) => setServiceForm({...serviceForm, pricePerM2: e.target.value})}
                required
                min="1"
              />
              <div className="form-actions">
                <button type="submit">{editingService ? 'Update Service' : 'Add Service'}</button>
                {editingService && (
                  <button type="button" onClick={handleCancelEdit} className="cancel-btn">Cancel</button>
                )}
              </div>
            </form>

            <div className="services-list">
              <h3>Current Services</h3>
              {services.length === 0 ? (
                <p className="empty-state">No services added yet. Add your first service above.</p>
              ) : (
                <table className="services-table">
                  <thead>
                    <tr>
                      <th>Service Name</th>
                      <th>Price per m²</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map(service => (
                      <tr key={service.id}>
                        <td>{service.serviceName}</td>
                        <td>{service.pricePerM2} EUR</td>
                        <td className="actions">
                          <button onClick={() => handleEditService(service)} className="edit-btn">✏️ Edit</button>
                          <button onClick={() => handleDeleteService(service.id)} className="delete-btn">🗑️ Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

        {activeSection === 'jobs' && (
          <section className="jobs-management">
            <h2>💼 Jobs Management</h2>
            <p>Manage job postings and view applications</p>

            <form onSubmit={editingJob ? handleUpdateJob : handleAddJob} className="job-form">
              <h3>{editingJob ? 'Edit Job' : 'Post New Job'}</h3>
              
              <input
                type="text"
                placeholder="Job Title (e.g., Construction Worker)"
                value={jobForm.title}
                onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                required
              />
              
              <textarea
                placeholder="Job Description"
                value={jobForm.description}
                onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                rows="4"
                required
              />
              
              <textarea
                placeholder="Requirements (optional)"
                value={jobForm.requirements}
                onChange={(e) => setJobForm({...jobForm, requirements: e.target.value})}
                rows="3"
              />
              
              <input
                type="text"
                placeholder="Salary/Pay Range (optional, e.g., 500-800 EUR/month)"
                value={jobForm.salary}
                onChange={(e) => setJobForm({...jobForm, salary: e.target.value})}
              />
              
              <input
                type="text"
                placeholder="Location (optional, e.g., Durres, Albania)"
                value={jobForm.location}
                onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
              />
              
              <select
                value={jobForm.type}
                onChange={(e) => setJobForm({...jobForm, type: e.target.value})}
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Temporary">Temporary</option>
              </select>
              
              <select
                value={jobForm.status}
                onChange={(e) => setJobForm({...jobForm, status: e.target.value})}
              >
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </select>
              
              <div className="form-actions">
                <button type="submit">{editingJob ? 'Update Job' : 'Post Job'}</button>
                {editingJob && (
                  <button type="button" onClick={handleCancelJobEdit} className="cancel-btn">Cancel</button>
                )}
              </div>
            </form>

            <div className="jobs-list">
              <h3>Current Job Postings ({jobs.length})</h3>
              {jobs.length === 0 ? (
                <p className="empty-state">No jobs posted yet. Add your first job above.</p>
              ) : (
                <div className="jobs-grid">
                  {jobs.map(job => (
                    <div key={job.id} className={`job-card ${job.status}`}>
                      <div className="job-header">
                        <h4>{job.title}</h4>
                        <span className={`status-badge ${job.status}`}>
                          {job.status === 'active' ? '🟢 Active' : '🔴 Closed'}
                        </span>
                      </div>
                      <p className="job-type">{job.type}</p>
                      {job.location && <p>📍 {job.location}</p>}
                      {job.salary && <p>💰 {job.salary}</p>}
                      <p className="job-description">{job.description.substring(0, 100)}...</p>
                      <div className="job-actions">
                        <button onClick={() => handleEditJob(job)} className="edit-btn">✏️ Edit</button>
                        <button onClick={() => handleDeleteJob(job.id)} className="delete-btn">🗑️ Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="applications-section">
              <h3>Job Applications ({jobApplications.length})</h3>
              {jobApplications.length === 0 ? (
                <p className="empty-state">No applications received yet.</p>
              ) : (
                <table className="applications-table">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>CV</th>
                      <th>Cover Letter</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobApplications.map(app => {
                      const job = jobs.find(j => j.id === app.jobId);
                      return (
                        <tr key={app.id}>
                          <td>{job?.title || 'N/A'}</td>
                          <td>{app.name}</td>
                          <td>{app.email}</td>
                          <td>{app.phone || 'N/A'}</td>
                          <td>
                            {app.cv ? (
                              <a href={`/uploads/${app.cv}`} target="_blank" rel="noopener noreferrer">📄 View</a>
                            ) : 'N/A'}
                          </td>
                          <td>
                            {app.coverLetter ? (
                              <a href={`/uploads/${app.coverLetter}`} target="_blank" rel="noopener noreferrer">📄 View</a>
                            ) : 'N/A'}
                          </td>
                          <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        )}

        {activeSection === 'partners' && (
          <>
            <section className="create-partner">
              <h2>Create New Partner</h2>
              <form onSubmit={handleCreatePartner}>
                <input
                  type="text"
                  placeholder="Username"
                  value={newPartner.username}
                  onChange={(e) => setNewPartner({...newPartner, username: e.target.value})}
                  required
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={newPartner.password}
                  onChange={(e) => setNewPartner({...newPartner, password: e.target.value})}
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newPartner.email}
                  onChange={(e) => setNewPartner({...newPartner, email: e.target.value})}
                  required
                />
                <input
                  type="text"
                  placeholder="Company Name"
                  value={newPartner.company}
                  onChange={(e) => setNewPartner({...newPartner, company: e.target.value})}
                  required
                />
                <button type="submit">Create Partner</button>
              </form>
            </section>

            <section className="partners-list">
              <h2>Partner Management</h2>
              <div className="partners-grid">
                {partners.map(partner => (
                  <div key={partner.id} className={`partner-card ${partner.status}`}>
                    <h3>{partner.username}</h3>
                    <p><strong>Company:</strong> {partner.company}</p>
                    <p><strong>Email:</strong> {partner.email}</p>
                    <p><strong>Status:</strong> 
                      <span className={`status ${partner.status}`}>
                        {partner.status === 'active' ? '🟢 Active' : '🔒 Locked'}
                      </span>
                    </p>
                    <p><strong>Created:</strong> {new Date(partner.createdAt).toLocaleDateString()}</p>
                    
                    <div className="partner-actions">
                      <button 
                        onClick={() => togglePartnerStatus(partner.id, partner.status)}
                        className={partner.status === 'active' ? 'lock-btn' : 'unlock-btn'}
                      >
                        {partner.status === 'active' ? '🔒 Lock' : '🔓 Unlock'}
                      </button>
                      <button 
                        onClick={() => deletePartner(partner.id)}
                        className="delete-btn"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {activeSection === 'messages' && (
          <div className="messages-container">
            <section className="messages-section">
              <h2>📧 Contact Messages ({contactMessages.length})</h2>
              {contactMessages.length === 0 ? (
                <p>No contact messages yet.</p>
              ) : (
                <div className="messages-list">
                  {[...contactMessages].reverse().map(message => (
                    <div key={message.id} className="message-card">
                      <div className="message-header">
                        <h3>{message.name}</h3>
                        <span className="message-date">
                          {new Date(message.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <p><strong>Email:</strong> {message.email}</p>
                      <p><strong>Message:</strong></p>
                      <p className="message-content">{message.message}</p>
                      <button 
                        onClick={() => deleteContactMessage(message.id)}
                        className="delete-btn"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="quote-requests-section">
              <h2>💰 Quote Requests ({quoteRequests.length})</h2>
              {quoteRequests.length === 0 ? (
                <p>No quote requests yet.</p>
              ) : (
                <div className="messages-list">
                  {[...quoteRequests].reverse().map(quote => (
                    <div key={quote.id} className="message-card quote-card">
                      <div className="message-header">
                        <h3>{quote.name}</h3>
                        <span className="message-date">
                          {new Date(quote.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <div className="quote-details">
                        <p><strong>Email:</strong> {quote.email}</p>
                        <p><strong>Phone:</strong> {quote.phone}</p>
                        <p><strong>Project Type:</strong> {quote.projectType}</p>
                        <p><strong>Budget:</strong> {quote.budget}</p>
                        <p><strong>Description:</strong></p>
                        <p className="message-content">{quote.description}</p>
                      </div>
                      <button 
                        onClick={() => deleteQuoteRequest(quote.id)}
                        className="delete-btn"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
